<section class="av-card p-4 d-flex flex-column gap-3">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <h2 class="av-section-title m-0">{{ 'backoffice.plans.list.title' | translate }}</h2>
      <p class="av-meta m-0" data-testid="plans-scope-note">
        {{ 'backoffice.plans.list.scope_note' | translate }}
      </p>
    </div>

    <div class="d-flex align-items-center gap-2">
      <button type="button" class="av-btn" [disabled]="facade.isBusy()" (click)="facade.reload()">
        <mat-icon aria-hidden="true">refresh</mat-icon>
        {{ 'backoffice.common.actions.refresh' | translate }}
      </button>

      @if (facade.canWrite()) {
        <button
          type="button"
          class="av-btn av-btn-primary"
          [disabled]="facade.isMutating()"
          (click)="openCreate()"
        >
          <mat-icon aria-hidden="true">add</mat-icon>
          {{ 'backoffice.plans.list.create' | translate }}
        </button>
      }
    </div>
  </div>

  @if (!facade.canWrite()) {
    <p class="av-meta m-0" data-testid="plans-restricted">
      {{ 'backoffice.plans.list.restricted_note' | translate }}
    </p>
  }

  <div class="filter-row d-flex flex-wrap align-items-end gap-2">
    <label class="d-flex flex-column gap-1 flex-grow-1" for="plan-search">
      <span class="av-meta">{{ 'backoffice.plans.list.filters.search' | translate }}</span>
      <input
        id="plan-search"
        type="search"
        class="av-field w-100"
        [value]="facade.filters().search"
        [matTooltip]="'backoffice.plans.list.search_hint' | translate"
        (change)="search($any($event.target).value)"
      />
    </label>

    <label class="d-flex flex-column gap-1" for="plan-status">
      <span class="av-meta">{{ 'backoffice.plans.list.filters.status' | translate }}</span>
      <select
        id="plan-status"
        class="av-field"
        [value]="facade.filters().status"
        (change)="filterStatus($any($event.target).value)"
      >
        <option value="">{{ 'backoffice.plans.list.filters.any_status' | translate }}</option>
        @for (option of statusOptions; track option) {
          <option [value]="option">{{ statusLabelKeys[option] | translate }}</option>
        }
      </select>
    </label>

    <label class="d-flex flex-column gap-1" for="plan-market-filter">
      <span class="av-meta">{{ 'backoffice.plans.list.filters.market' | translate }}</span>
      <select
        id="plan-market-filter"
        class="av-field"
        [value]="facade.filters().market_id"
        (change)="filterMarket($any($event.target).value)"
      >
        <option value="">{{ 'backoffice.plans.list.filters.all_markets' | translate }}</option>
        @for (market of facade.markets(); track market.id) {
          <option [value]="market.id">{{ market.displayName }}</option>
        }
      </select>
    </label>

    @if (facade.hasActiveFilters()) {
      <button type="button" class="av-btn" (click)="clearFilters()">
        {{ 'backoffice.plans.list.filters.clear' | translate }}
      </button>
    }
  </div>
</section>

<section class="av-card p-4 mt-3 d-flex flex-column">
  @switch (facade.state()) {
    @case ('error') {
      <av-empty-state
        icon="error_outline"
        [title]="'backoffice.plans.list.error.title' | translate"
        [message]="'backoffice.plans.list.error.message' | translate"
      ></av-empty-state>
    }
    @case ('ready') {
      @if (facade.isEmpty()) {
        <av-empty-state
          icon="sell"
          [title]="emptyTitleKey() | translate"
          [message]="emptyMessageKey() | translate"
        ></av-empty-state>
      } @else {
        <div class="table-scroll">
          <table class="av-table w-100" data-testid="plans-table">
            <thead>
              <tr>
                <th scope="col">{{ 'backoffice.plans.list.columns.plan' | translate }}</th>
                <th scope="col">{{ 'backoffice.plans.list.columns.market' | translate }}</th>
                <th scope="col">{{ 'backoffice.plans.list.columns.version' | translate }}</th>
                <th scope="col">{{ 'backoffice.plans.list.columns.status' | translate }}</th>
                <th scope="col">
                  {{ 'backoffice.plans.list.columns.agent_component' | translate }}
                </th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.plans.list.columns.subscribers' | translate }}
                </th>
                <th scope="col">{{ 'backoffice.plans.list.columns.updated' | translate }}</th>
                <th scope="col" class="text-end">
                  {{ 'backoffice.plans.list.columns.actions' | translate }}
                </th>
              </tr>
            </thead>

            <tbody>
              @for (plan of facade.plans(); track plan.id) {
                <tr>
                  <td>
                    <div class="d-flex flex-column">
                      <span class="cell-strong">{{ plan.displayName }}</span>
                      <span class="av-meta">{{ plan.code || unresolved }}</span>
                    </div>
                  </td>

                  <td class="cell-muted">{{ plan.marketLabel }}</td>

                  <td class="cell-muted">{{ plan.version }}</td>

                  <td>
                    <av-status-chip
                      [label]="
                        (plan.is_active ? 'backoffice.common.active' : 'backoffice.common.inactive')
                          | translate
                      "
                      [tone]="plan.is_active ? 'positive' : 'neutral'"
                    ></av-status-chip>
                  </td>

                  <td>
                    @if (hasAgent(plan)) {
                      <av-status-chip
                        [label]="'backoffice.common.present' | translate"
                        tone="positive"
                      ></av-status-chip>
                    } @else {
                      <av-status-chip
                        [label]="'backoffice.common.absent' | translate"
                        tone="neutral"
                      ></av-status-chip>
                    }
                  </td>

                  <td class="text-end">{{ plan.subscriber_count }}</td>

                  <td class="cell-muted">
                    {{ plan.updated_at ? (plan.updated_at | date: dateFormat) : unresolved }}
                  </td>

                  <td class="text-end">
                    <button type="button" class="av-btn" (click)="openPlan(plan)">
                      <mat-icon aria-hidden="true">open_in_new</mat-icon>
                      {{ 'backoffice.common.actions.open' | translate }}
                    </button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>

        <av-paginator
          [page]="facade.page()"
          [pageSize]="facade.pageSize()"
          [total]="facade.total()"
          [disabled]="facade.isBusy()"
          (pageChange)="goToPage($event)"
          (pageSizeChange)="changePageSize($event)"
        ></av-paginator>
      }
    }
    @default {
      <av-empty-state
        icon="hourglass_empty"
        [title]="'backoffice.plans.list.loading.title' | translate"
        [message]="'backoffice.plans.list.loading.message' | translate"
      ></av-empty-state>
    }
  }
</section>
